import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api.js';
import CrewMovementsCard from '../../components/CrewMovementsCard.jsx';

function fmtDate(d) {
  return d ? new Date(d).toLocaleDateString() : '—';
}

/** A DATE column value (ISO string) as the YYYY-MM-DD a date input expects. */
function toInputDate(d) {
  if (!d) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
}

const EMPTY_MEMBER = {
  name: '',
  position: 'Crew',
  marinaLicenseNo: '',
  marinaLicenseExpiry: '',
  cocExpiry: '',
  userId: '',
};

/** Licence / certificate fields from a form, '' → null, for create / update. */
function certFields(m) {
  return {
    marinaLicenseNo: m.marinaLicenseNo.trim() || null,
    marinaLicenseExpiry: m.marinaLicenseExpiry || null,
    cocExpiry: m.cocExpiry || null,
  };
}

export default function CrewRosterPage() {
  const [members, setMembers] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [tugs, setTugs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [pageMsg, setPageMsg] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({ crewMemberId: '', tugId: '', shiftDate: '', notes: '' });
  // 8936: the shift being edited in the same modal; null when assigning a new one.
  const [editingShift, setEditingShift] = useState(null);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [member, setMember] = useState(EMPTY_MEMBER);
  const [addingMember, setAddingMember] = useState(false);
  // Edit-member modal (8789): the member being edited, its form and errors.
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_MEMBER);
  const [editError, setEditError] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  // Tug Captain logins a captain's crew record can be linked to (Captain Console, 8794).
  const [captainLogins, setCaptainLogins] = useState([]);

  const addMember = async (e) => {
    e.preventDefault();
    if (!member.name.trim()) {
      setPageMsg({ type: 'danger', text: 'A crew member needs a name.' });
      return;
    }
    setAddingMember(true);
    try {
      await api.createCrewMember({
        name: member.name.trim(),
        position: member.position,
        ...certFields(member),
      });
      await load();
      setPageMsg({ type: 'success', text: `${member.name.trim()} added as ${member.position}.` });
      setMember(EMPTY_MEMBER);
    } catch (err) {
      setPageMsg({ type: 'danger', text: err.details?.[0]?.message || err.message || 'Failed to add the crew member.' });
    } finally {
      setAddingMember(false);
    }
  };

  const openEdit = (m) => {
    setEditError('');
    setEditing(m);
    setEditForm({
      name: m.name || '',
      position: m.position || 'Crew',
      marinaLicenseNo: m.marina_license_no || '',
      marinaLicenseExpiry: toInputDate(m.marina_license_expiry),
      cocExpiry: toInputDate(m.coc_expiry),
      userId: m.user_id || '',
    });
    api.listCaptainLogins().then((rows) => setCaptainLogins(rows || []), () => setCaptainLogins([]));
  };

  const setEditField = (key, value) => {
    setEditForm((f) => ({ ...f, [key]: value }));
    setEditError('');
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editForm.name.trim()) {
      setEditError('A crew member needs a name.');
      return;
    }
    setSavingEdit(true);
    try {
      await api.updateCrewMember(editing.id, {
        name: editForm.name.trim(),
        position: editForm.position,
        ...certFields(editForm),
        // Only captains run the Captain Console; changing position drops the link.
        userId: editForm.position === 'Captain' ? editForm.userId || null : null,
      });
      await load();
      setPageMsg({ type: 'success', text: `${editForm.name.trim()} updated.` });
      setEditing(null);
    } catch (err) {
      setEditError(err.message || 'Failed to update the crew member.');
    } finally {
      setSavingEdit(false);
    }
  };

  const load = useCallback(async () => {
    const [membersRes, shiftsRes, tugsRes] = await Promise.all([
      api.listCrewMembers(),
      api.listCrewShifts(),
      api.listTugs(),
    ]);
    setMembers(membersRes || []);
    setShifts(shiftsRes || []);
    setTugs(tugsRes || []);
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        await load();
      } catch (err) {
        setLoadError(err.message || 'Failed to load crew roster.');
      } finally {
        setLoading(false);
      }
    })();
  }, [load]);

  const captains = members.filter((m) => m.position === 'Captain');
  const [alertingId, setAlertingId] = useState(null);

  const triggerExpiryAlert = async (captain) => {
    setAlertingId(captain.id);
    setPageMsg(null);
    try {
      const res = await api.triggerCaptainExpiryAlert(captain.id);
      setPageMsg({
        type: 'info',
        text: `SMS expiry reminder sent to ${res.captain} (${res.to || 'no phone'}) — ${res.message}`,
      });
    } catch (err) {
      setPageMsg({ type: 'danger', text: err.message || 'Failed to trigger expiry alert.' });
    } finally {
      setAlertingId(null);
    }
  };

  const statusBadge = (status) => {
    if (status === 'Expired') return 'bg-danger';
    if (status === 'Expiring Soon') return 'bg-warning text-dark';
    return 'bg-success';
  };

  const openAssign = () => {
    setFormError('');
    setEditingShift(null);
    setForm({ crewMemberId: '', tugId: '', shiftDate: '', notes: '' });
    setModalOpen(true);
  };

  const openEditShift = (shift) => {
    setFormError('');
    setEditingShift(shift);
    setForm({
      crewMemberId: shift.crew_member_id,
      tugId: shift.tug_id || '',
      shiftDate: toInputDate(shift.shift_date),
      notes: shift.notes || '',
    });
    setModalOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!form.crewMemberId || !form.shiftDate) {
      setFormError('Select a crew member and a shift date.');
      return;
    }
    setSaving(true);
    try {
      const fields = { tugId: form.tugId || null, shiftDate: form.shiftDate, notes: form.notes.trim() || null };
      if (editingShift) {
        const saved = await api.updateCrewShift(editingShift.id, fields);
        setPageMsg({
          type: 'success',
          text: `Shift updated: ${saved.crew_member_name} on ${saved.tug_name || 'no tug'}, ${fmtDate(saved.shift_date)}.`,
        });
      } else {
        await api.assignCrewShift({ crewMemberId: form.crewMemberId, ...fields });
        setPageMsg({ type: 'success', text: 'Crew shift assigned.' });
      }
      await load();
      setModalOpen(false);
    } catch (err) {
      // Certification compliance rejections surface here (AC#3).
      setFormError(err.message || 'Failed to assign shift.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container-fluid py-4" data-testid="crew-page">
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center bg-white p-3 rounded shadow-sm">
            <div>
              <h3 className="text-primary mb-0">
                <i className="fas fa-users-cog me-2" aria-hidden="true" />
                Crew Rosters &amp; Compliance
              </h3>
              <p className="text-muted mb-0 small">
                Assign captain and crew shifts to tugs — expired certifications block rostering.
              </p>
            </div>
            <button className="btn btn-primary" onClick={openAssign} data-testid="assign-shift">
              <i className="fas fa-calendar-plus me-1" aria-hidden="true" />
              Assign Crew Shift
            </button>
          </div>
        </div>
      </div>

      {loadError && (
        <div className="alert alert-danger" data-testid="crew-load-error">
          {loadError}
        </div>
      )}
      {pageMsg && (
        <div className={`alert alert-${pageMsg.type}`} role="alert" data-testid="crew-message">
          {pageMsg.text}
        </div>
      )}

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
        </div>
      ) : (
        <div className="row">
          <div className="col-12 mb-4">
            <form className="card border-0 shadow-sm" onSubmit={addMember} noValidate data-testid="add-member-form">
              <div className="card-body row g-2 align-items-end">
                <div className="col-md-3">
                  <label htmlFor="member-name" className="form-label small fw-bold text-muted">New crew member</label>
                  <input
                    id="member-name"
                    className="form-control form-control-sm"
                    placeholder="Full name"
                    maxLength={160}
                    value={member.name}
                    onChange={(e) => setMember({ ...member, name: e.target.value })}
                    data-testid="member-name"
                  />
                </div>
                <div className="col-md-2">
                  <label htmlFor="member-position" className="form-label small fw-bold text-muted">Position</label>
                  <select
                    id="member-position"
                    className="form-select form-select-sm"
                    value={member.position}
                    onChange={(e) => setMember({ ...member, position: e.target.value })}
                    data-testid="member-position"
                  >
                    <option value="Captain">Captain</option>
                    <option value="Chief Engineer">Chief Engineer</option>
                    <option value="Crew">Crew</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <label htmlFor="member-license-no" className="form-label small fw-bold text-muted">MARINA licence no.</label>
                  <input
                    id="member-license-no"
                    className="form-control form-control-sm"
                    maxLength={60}
                    value={member.marinaLicenseNo}
                    onChange={(e) => setMember({ ...member, marinaLicenseNo: e.target.value })}
                    data-testid="member-license-no"
                  />
                </div>
                <div className="col-md-2">
                  <label htmlFor="member-license-expiry" className="form-label small fw-bold text-muted">Licence expiry</label>
                  <input
                    id="member-license-expiry"
                    type="date"
                    className="form-control form-control-sm"
                    value={member.marinaLicenseExpiry}
                    onChange={(e) => setMember({ ...member, marinaLicenseExpiry: e.target.value })}
                    data-testid="member-license-expiry"
                  />
                </div>
                <div className="col-md-2">
                  <label htmlFor="member-coc-expiry" className="form-label small fw-bold text-muted">COC expiry</label>
                  <input
                    id="member-coc-expiry"
                    type="date"
                    className="form-control form-control-sm"
                    value={member.cocExpiry}
                    onChange={(e) => setMember({ ...member, cocExpiry: e.target.value })}
                    data-testid="member-coc-expiry"
                  />
                </div>
                <div className="col-md-1">
                  <button type="submit" className="btn btn-outline-primary btn-sm w-100" disabled={addingMember} data-testid="add-member">
                    Add
                  </button>
                </div>
              </div>
            </form>
          </div>
          <div className="col-12 mb-4">
            <CrewMovementsCard members={members} tugs={tugs} />
          </div>
          {/* LEFT: active crew shifts */}
          <div className="col-lg-6 mb-4">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white pt-4 border-bottom-0">
                <h5 className="mb-0 text-secondary">
                  <i className="fas fa-clipboard-list me-2" aria-hidden="true" />
                  Active Crew Shifts
                </h5>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="bg-light text-secondary small text-uppercase">
                      <tr>
                        <th>Crew Member</th>
                        <th>Position</th>
                        <th>Assigned Tug</th>
                        <th>Shift Date</th>
                        <th aria-label="action" />
                      </tr>
                    </thead>
                    <tbody>
                      {shifts.length === 0 && (
                        <tr>
                          <td colSpan={5} className="text-center text-muted py-4">
                            No shifts rostered yet.
                          </td>
                        </tr>
                      )}
                      {shifts.map((s) => (
                        <tr key={s.id} data-testid="shift-row" data-crew={s.crew_member_name}>
                          <td className="fw-bold">{s.crew_member_name}</td>
                          <td>{s.crew_member_position}</td>
                          <td>{s.tug_name || '—'}</td>
                          <td>{fmtDate(s.shift_date)}</td>
                          <td className="text-end">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => openEditShift(s)}
                              aria-label={`Edit shift of ${s.crew_member_name}`}
                              data-testid="edit-shift"
                            >
                              <i className="fas fa-pen me-1" aria-hidden="true" />
                              Edit
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT: captain license & certification tracker */}
          <div className="col-lg-6 mb-4">
            <div className="card border-0 shadow-sm h-100" data-testid="cert-tracker">
              <div className="card-header bg-white pt-4 border-bottom-0">
                <h5 className="mb-0 text-secondary">
                  <i className="fas fa-certificate me-2 text-warning" aria-hidden="true" />
                  Captain License &amp; Certification Tracker
                </h5>
                <p className="text-muted small mb-0">
                  MARINA COI / COC validity. Alerts fire 30, 15 and 5 days before expiry.
                </p>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="bg-light text-secondary small text-uppercase">
                      <tr>
                        <th>Captain</th>
                        <th>License No.</th>
                        <th>COC Expiry</th>
                        <th>Status</th>
                        <th aria-label="action" />
                      </tr>
                    </thead>
                    <tbody>
                      {captains.length === 0 && (
                        <tr>
                          <td colSpan={5} className="text-center text-muted py-4">
                            No captains registered.
                          </td>
                        </tr>
                      )}
                      {captains.map((c) => {
                        const status = c.certStatus?.status || (c.compliance.compliant ? 'Valid' : 'Expired');
                        return (
                          <tr
                            key={c.id}
                            className={c.compliance.compliant ? '' : 'table-danger'}
                            data-testid="captain-row"
                            data-name={c.name}
                            data-compliant={c.compliance.compliant ? 'true' : 'false'}
                            data-status={status}
                          >
                            <td className="fw-bold">
                              {c.name}
                              <span className="d-block text-muted small">
                                MARINA exp. {fmtDate(c.marina_license_expiry)}
                              </span>
                            </td>
                            <td className="small">{c.marina_license_no || '—'}</td>
                            <td className="small">
                              {fmtDate(c.coc_expiry)}
                              {c.certStatus?.minDaysToExpiry != null && status !== 'Expired' && (
                                <span className="d-block text-muted">
                                  in {c.certStatus.minDaysToExpiry}d
                                </span>
                              )}
                            </td>
                            <td>
                              <span className={`badge ${statusBadge(status)}`} data-testid="cert-status">
                                {status === 'Expired' && (
                                  <i className="fas fa-triangle-exclamation me-1" aria-hidden="true" />
                                )}
                                {status}
                              </span>
                              {!c.compliance.compliant && (
                                <span className="d-none" data-testid="captain-noncompliant">
                                  Expired: {c.compliance.expired.join(', ')}
                                </span>
                              )}
                            </td>
                            <td className="text-end text-nowrap">
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary me-1"
                                onClick={() => openEdit(c)}
                                aria-label={`Edit ${c.name}`}
                                data-testid="edit-member"
                              >
                                <i className="fas fa-pen me-1" aria-hidden="true" />
                                Edit
                              </button>
                              {status !== 'Valid' && (
                                <button
                                  className="btn btn-sm btn-outline-warning"
                                  onClick={() => triggerExpiryAlert(c)}
                                  disabled={alertingId === c.id}
                                  data-testid="trigger-expiry-alert"
                                >
                                  {alertingId === c.id && (
                                    <span
                                      className="spinner-border spinner-border-sm me-1"
                                      role="status"
                                      aria-hidden="true"
                                    />
                                  )}
                                  <i className="fas fa-bell me-1" aria-hidden="true" />
                                  Trigger Expiry Alert
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assign shift modal */}
      {modalOpen && (
        <>
          <div className="modal d-block" tabIndex={-1} role="dialog" data-testid="shift-modal">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <form onSubmit={submit} noValidate>
                  <div className="modal-header">
                    <h5 className="modal-title">
                      <i className="fas fa-calendar-plus me-2 text-primary" aria-hidden="true" />
                      {editingShift ? 'Update Crew Shift' : 'Assign Crew Shift'}
                    </h5>
                    <button type="button" className="btn-close" aria-label="Close" onClick={() => setModalOpen(false)} />
                  </div>
                  <div className="modal-body">
                    {formError && (
                      <div className="alert alert-danger" role="alert" data-testid="shift-error">
                        {formError}
                      </div>
                    )}
                    <div className="mb-3">
                      <label htmlFor="s-crew" className="form-label small fw-bold text-muted">
                        Crew Member <span className="text-danger">*</span>
                      </label>
                      <select
                        id="s-crew"
                        className="form-select form-select-sm"
                        value={form.crewMemberId}
                        onChange={(e) => setForm({ ...form, crewMemberId: e.target.value })}
                        disabled={Boolean(editingShift)}
                        data-testid="shift-crew"
                      >
                        <option value="">Select a crew member…</option>
                        {members.map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.name} ({m.position})
                            {m.compliance.applicable && !m.compliance.compliant ? ' — CERT EXPIRED' : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="mb-3">
                      <label htmlFor="s-tug" className="form-label small fw-bold text-muted">
                        Assigned Tug
                      </label>
                      <select
                        id="s-tug"
                        className="form-select form-select-sm"
                        value={form.tugId}
                        onChange={(e) => setForm({ ...form, tugId: e.target.value })}
                        data-testid="shift-tug"
                      >
                        <option value="">— None —</option>
                        {tugs.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="mb-1">
                      <label htmlFor="s-date" className="form-label small fw-bold text-muted">
                        Shift Date <span className="text-danger">*</span>
                      </label>
                      <input
                        id="s-date"
                        type="date"
                        className="form-control form-control-sm"
                        value={form.shiftDate}
                        onChange={(e) => setForm({ ...form, shiftDate: e.target.value })}
                        data-testid="shift-date"
                      />
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setModalOpen(false)}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary btn-sm" disabled={saving} data-testid="submit-shift">
                      {saving && (
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      )}
                      <i className="fas fa-check me-1" aria-hidden="true" />
                      {editingShift ? 'Save Changes' : 'Assign Shift'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}
      {/* Edit crew member modal (8789) */}
      {editing && (
        <>
          <div className="modal d-block" tabIndex={-1} role="dialog" data-testid="edit-member-modal">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <form onSubmit={saveEdit} noValidate>
                  <div className="modal-header">
                    <h5 className="modal-title">
                      <i className="fas fa-user-pen me-2 text-primary" aria-hidden="true" />
                      Edit {editing.name}
                    </h5>
                    <button type="button" className="btn-close" aria-label="Close" onClick={() => setEditing(null)} />
                  </div>
                  <div className="modal-body">
                    {editError && (
                      <div className="alert alert-danger" role="alert" data-testid="edit-member-error">
                        {editError}
                      </div>
                    )}
                    <div className="mb-3">
                      <label htmlFor="e-name" className="form-label small fw-bold text-muted">
                        Name <span className="text-danger">*</span>
                      </label>
                      <input
                        id="e-name"
                        className="form-control form-control-sm"
                        maxLength={160}
                        value={editForm.name}
                        onChange={(e) => setEditField('name', e.target.value)}
                        data-testid="edit-member-name"
                      />
                    </div>
                    <div className="mb-3">
                      <label htmlFor="e-position" className="form-label small fw-bold text-muted">Position</label>
                      <select
                        id="e-position"
                        className="form-select form-select-sm"
                        value={editForm.position}
                        onChange={(e) => setEditField('position', e.target.value)}
                        data-testid="edit-member-position"
                      >
                        <option value="Captain">Captain</option>
                        <option value="Chief Engineer">Chief Engineer</option>
                        <option value="Crew">Crew</option>
                      </select>
                    </div>
                    <div className="mb-3">
                      <label htmlFor="e-license-no" className="form-label small fw-bold text-muted">MARINA licence no.</label>
                      <input
                        id="e-license-no"
                        className="form-control form-control-sm"
                        maxLength={60}
                        value={editForm.marinaLicenseNo}
                        onChange={(e) => setEditField('marinaLicenseNo', e.target.value)}
                        data-testid="edit-member-license-no"
                      />
                    </div>
                    {editForm.position === 'Captain' && (
                      <div className="mb-3">
                        <label htmlFor="e-login" className="form-label small fw-bold text-muted">Linked login</label>
                        <select
                          id="e-login"
                          className="form-select form-select-sm"
                          value={editForm.userId}
                          onChange={(e) => setEditField('userId', e.target.value)}
                          data-testid="edit-member-login"
                        >
                          <option value="">Not linked</option>
                          {captainLogins.map((u) => (
                            <option key={u.id} value={u.id}>
                              {u.name} ({u.email})
                            </option>
                          ))}
                        </select>
                        <div className="form-text">
                          The Tug Captain login this captain signs in with. Jobs assigned to them show on that Captain Console.
                        </div>
                      </div>
                    )}
                    <div className="row g-2 mb-1">
                      <div className="col-6">
                        <label htmlFor="e-license-expiry" className="form-label small fw-bold text-muted">Licence expiry</label>
                        <input
                          id="e-license-expiry"
                          type="date"
                          className="form-control form-control-sm"
                          value={editForm.marinaLicenseExpiry}
                          onChange={(e) => setEditField('marinaLicenseExpiry', e.target.value)}
                          data-testid="edit-member-license-expiry"
                        />
                      </div>
                      <div className="col-6">
                        <label htmlFor="e-coc-expiry" className="form-label small fw-bold text-muted">COC expiry</label>
                        <input
                          id="e-coc-expiry"
                          type="date"
                          className="form-control form-control-sm"
                          value={editForm.cocExpiry}
                          onChange={(e) => setEditField('cocExpiry', e.target.value)}
                          data-testid="edit-member-coc-expiry"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setEditing(null)}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary btn-sm" disabled={savingEdit} data-testid="save-member">
                      {savingEdit && (
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      )}
                      <i className="fas fa-check me-1" aria-hidden="true" />
                      Save
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}
    </div>
  );
}
